import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "@/components/ui/combobox";
import { useCompanyList } from "@/features/company/hooks/useCompanies";
import type {
  CompanySearchRequest,
  DispatchCompanyListItem,
} from "@/features/company/types/company.types";

interface CompanySelectFieldProps {
  onSelect: (company: DispatchCompanyListItem) => void;
  selectedName?: string;
}

interface ComboboxChangeEventDetails {
  reason: string;
}

const COMPANY_SEARCH_PAGE_SIZE = 20;

export function CompanySelectField({
  onSelect,
  selectedName,
}: CompanySelectFieldProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] =
    useState<DispatchCompanyListItem | null>(null);

  // 사용자가 새 업체를 고르면 그 선택이 우선. 미선택 상태에선 초기 표시값(selectedName) 사용.
  const selectedCompanyName =
    selectedCompany?.companyName ?? selectedName ?? "";

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => window.clearTimeout(timerId);
  }, [query]);

  const searchParams = useMemo<CompanySearchRequest>(
    () => ({
      page: 1,
      pageSize: COMPANY_SEARCH_PAGE_SIZE,
      searchStr: debouncedQuery.trim(),
      isActive: null,
    }),
    [debouncedQuery],
  );

  const { data, isLoading } = useCompanyList(searchParams);
  const companies = data?.dispatchCompanies ?? [];

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
    }
  }, []);

  const handleInputValueChange = useCallback(
    (nextInputValue: string, eventDetails: ComboboxChangeEventDetails) => {
      if (
        eventDetails.reason !== "input-change" &&
        eventDetails.reason !== "input-clear" &&
        eventDetails.reason !== "clear-press"
      ) {
        return;
      }

      setQuery(nextInputValue);
    },
    [],
  );

  const handleSelect = useCallback(
    (company: DispatchCompanyListItem | null) => {
      if (!company) {
        return;
      }

      setSelectedCompany(company);
      setQuery("");
      setDebouncedQuery("");
      setIsOpen(false);
      onSelect(company);
    },
    [onSelect],
  );

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        기존 배차 업체
      </label>
      <Combobox<DispatchCompanyListItem>
        open={isOpen}
        onOpenChange={handleOpenChange}
        value={selectedCompany}
        onValueChange={handleSelect}
        inputValue={query}
        onInputValueChange={handleInputValueChange}
        items={companies}
        filter={null}
        itemToStringLabel={(company) => company.companyName}
        itemToStringValue={(company) => company.companyGuid}
        isItemEqualToValue={(item, value) =>
          item.companyGuid === value.companyGuid
        }
      >
        <ComboboxTrigger className="flex h-8 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
          <span className="truncate">
            <ComboboxValue>
              {selectedCompanyName || "업체 선택"}
            </ComboboxValue>
          </span>
        </ComboboxTrigger>
        <ComboboxContent className="border border-gray-200 bg-white shadow-md">
          <div className="border-b border-gray-100 p-1">
            <ComboboxInput
              showTrigger={false}
              placeholder="업체명, 사업자번호, 전화번호 검색"
              className="h-8 w-full border-gray-200 text-xs shadow-none focus-within:border-blue-500 [&_[data-slot=input]]:h-8 [&_[data-slot=input]]:px-3 [&_[data-slot=input]]:text-xs"
            />
          </div>
          {isLoading ? (
            <div className="px-3 py-2 text-xs text-gray-400">검색 중...</div>
          ) : (
            <>
              <ComboboxList className="max-h-60 overflow-y-auto">
                {(company: DispatchCompanyListItem, index) => (
                  <ComboboxItem
                    key={company.companyGuid}
                    value={company}
                    index={index}
                    className="px-3 py-2 text-xs hover:bg-gray-50"
                  >
                    <div className="min-w-0">
                      <span className="block truncate font-medium text-gray-800">
                        {company.companyName}
                      </span>
                      <span className="block truncate text-gray-500">
                        {company.businessRegNum ?? "-"} ·{" "}
                        {company.representativeName ?? "-"}
                      </span>
                    </div>
                  </ComboboxItem>
                )}
              </ComboboxList>
              <ComboboxEmpty className="py-2 text-xs text-gray-400">
                검색 결과가 없습니다
              </ComboboxEmpty>
            </>
          )}
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
